import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CalculatorIcon,
  ClockIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { purchaseService } from '../../services/purchaseService'
import { supplierService } from '../../services/supplierService'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'

interface PurchaseItem {
  id: string
  product_id: number | null
  product_name: string
  quantity: number
  unit_price: number
  total: number
  serials: string[]
  // Calculator fields
  shipping_allocated: number
  landed_cost: number
  suggested_retail_price: number
  suggested_wholesale_price: number
  margin_percent: number
  // Price history
  price_history?: any[]
}

const COMMON_CURRENCIES = [
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
]

export const CreatePurchasePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialSupplierId = searchParams.get('supplier')

  const [supplierId, setSupplierId] = useState(initialSupplierId || '')
  const [currencyCode, setCurrencyCode] = useState('KES')
  const [exchangeRate, setExchangeRate] = useState(1)
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().split('T')[0])
  const [shippingCost, setShippingCost] = useState(0)
  const [items, setItems] = useState<PurchaseItem[]>([])
  const [showCalculator, setShowCalculator] = useState(false)
  const [showPriceHistory, setShowPriceHistory] = useState(false)
  const [selectedProductForHistory, setSelectedProductForHistory] = useState<string | null>(null)
  const [globalMargin, setGlobalMargin] = useState(30)

  const [currentItem, setCurrentItem] = useState({
    product_id: null as number | null,
    product_name: '',
    quantity: 1,
    unit_price: 0,
    serials: [] as string[],
    margin_percent: 30,
  })
  const [serialInput, setSerialInput] = useState('')

  // Supplier search
  const [supplierSearch, setSupplierSearch] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const supplierDropdownRef = useRef<HTMLDivElement>(null)

  // Product search
  const [productSearch, setProductSearch] = useState('')
  const [showProductDropdown, setShowProductDropdown] = useState(false)
  const productDropdownRef = useRef<HTMLDivElement>(null)

  // Currency search
  const [currencySearch, setCurrencySearch] = useState('')
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
  const currencyDropdownRef = useRef<HTMLDivElement>(null)

  const filteredCurrencies = currencySearch
    ? COMMON_CURRENCIES.filter(c =>
      c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
      c.name.toLowerCase().includes(currencySearch.toLowerCase())
    )
    : COMMON_CURRENCIES

  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => supplierService.getAll({ limit: 100 }),
  })

  const { data: supplierData } = useQuery({
    queryKey: ['supplier', supplierId],
    queryFn: () => supplierService.getById(supplierId),
    enabled: !!supplierId,
  })

  const { data: productHistoryData } = useQuery({
    queryKey: ['supplier-product-history', supplierId, selectedProductForHistory],
    queryFn: () => purchaseService.getProductHistory(supplierId, selectedProductForHistory!),
    enabled: !!supplierId && !!selectedProductForHistory,
  })

  // Fetch purchase history for the currently selected product (all suppliers)
  const { data: selectedProductHistoryData, isLoading: loadingProductHistory } = useQuery({
    queryKey: ['product-purchase-history', currentItem.product_id],
    queryFn: () => productService.getPurchaseHistory(currentItem.product_id!),
    enabled: !!currentItem.product_id,
  })

  const supplier = supplierData?.data?.data
  const productHistory = productHistoryData?.data?.data || []
  const selectedProductHistory = selectedProductHistoryData?.data?.data?.history || []
  const selectedProductHistoryStats = selectedProductHistoryData?.data?.data?.stats || null

  const suppliers: any[] = suppliersData?.data?.data?.items || []
  const filteredSuppliers = supplierSearch
    ? suppliers.filter((s: any) =>
      s.company_name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.supplier_code?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.email?.toLowerCase().includes(supplierSearch.toLowerCase())
    )
    : suppliers

  const selectedSupplier = suppliers.find((s: any) => s.supplier_id.toString() === supplierId)

  // Update currency when supplier changes
  useEffect(() => {
    if (supplier) {
      const newCurrency = supplier.default_currency_code || 'KES'
      setCurrencyCode(newCurrency)
      setExchangeRate(Number(supplier.currencies?.exchange_rate) || 1)
    }
  }, [supplier])

  // Fetch products for search
  const { data: productsData } = useQuery({
    queryKey: ['products', productSearch],
    queryFn: () => productService.search(productSearch),
    enabled: productSearch.length >= 2,
  })

  const products: any[] = productsData?.data?.data || []

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
        setShowSupplierDropdown(false)
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false)
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const createMutation = useMutation({
    mutationFn: (data: any) => purchaseService.create(data),
    onSuccess: (response) => {
      toast.success('Purchase created successfully')
      navigate(`/purchases/${response.data.data.purchase_id}`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create purchase')
    },
  })

  const calculateItemPrices = (item: typeof currentItem, shippingPerUnit: number) => {
    const unitPriceBase = item.unit_price * exchangeRate
    const landedCost = unitPriceBase + (shippingPerUnit * exchangeRate)
    const margin = item.margin_percent / 100
    const suggestedRetail = landedCost * (1 + margin)
    const suggestedWholesale = landedCost * (1 + margin * 0.7)

    return {
      unitPriceBase,
      landedCost,
      suggestedRetail,
      suggestedWholesale,
    }
  }

  const addItem = () => {
    if (!currentItem.product_name) {
      toast.error('Please enter product name')
      return
    }

    const lineTotal = currentItem.quantity * currentItem.unit_price

    // Calculate shipping per unit (will be recalculated when all items are added)
    const shippingPerUnit = items.length === 0 && shippingCost === 0
      ? 0
      : shippingCost / (items.reduce((sum, i) => sum + i.quantity, 0) + currentItem.quantity)

    const prices = calculateItemPrices(currentItem, shippingPerUnit)

    const newItem: PurchaseItem = {
      id: Date.now().toString(),
      product_id: currentItem.product_id,
      product_name: currentItem.product_name,
      quantity: currentItem.quantity,
      unit_price: currentItem.unit_price,
      total: lineTotal,
      serials: currentItem.serials,
      shipping_allocated: shippingPerUnit * currentItem.quantity,
      landed_cost: prices.landedCost,
      suggested_retail_price: prices.suggestedRetail,
      suggested_wholesale_price: prices.suggestedWholesale,
      margin_percent: currentItem.margin_percent,
    }

    setItems(prev => [...prev, newItem])
    setCurrentItem({
      product_id: null,
      product_name: '',
      quantity: 1,
      unit_price: 0,
      serials: [],
      margin_percent: globalMargin,
    })
    setProductSearch('')
    setSerialInput('')
  }

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id))
  }

  const addSerial = () => {
    if (!serialInput.trim()) return
    setCurrentItem(prev => ({
      ...prev,
      serials: [...prev.serials, serialInput.trim()],
    }))
    setSerialInput('')
  }

  const removeSerial = (index: number) => {
    setCurrentItem(prev => ({
      ...prev,
      serials: prev.serials.filter((_, i) => i !== index),
    }))
  }

  // Recalculate all items when shipping cost changes
  useEffect(() => {
    if (items.length === 0) return

    const totalLineTotal = items.reduce((sum, item) => sum + item.total, 0)

    setItems(prev => prev.map(item => {
      // Allocate shipping based on proportion of line total
      const proportion = totalLineTotal > 0 ? item.total / totalLineTotal : 0
      const itemShipping = shippingCost * proportion
      const shippingPerUnit = itemShipping / item.quantity

      const prices = calculateItemPrices(
        { ...item, unit_price: item.unit_price, margin_percent: item.margin_percent, quantity: item.quantity },
        shippingPerUnit
      )

      return {
        ...item,
        shipping_allocated: itemShipping,
        landed_cost: prices.landedCost,
        suggested_retail_price: prices.suggestedRetail,
        suggested_wholesale_price: prices.suggestedWholesale,
      }
    }))
  }, [shippingCost, exchangeRate])

  // Apply global margin to all items
  const applyGlobalMargin = () => {
    setItems(prev => prev.map(item => {
      const prices = calculateItemPrices(
        { ...item, unit_price: item.unit_price, margin_percent: globalMargin, quantity: item.quantity },
        item.shipping_allocated / item.quantity
      )
      return {
        ...item,
        margin_percent: globalMargin,
        suggested_retail_price: prices.suggestedRetail,
        suggested_wholesale_price: prices.suggestedWholesale,
      }
    }))
    toast.success('Margin applied to all items')
  }

  const subtotal = items.reduce((sum, item) => sum + item.total, 0)
  const tax = subtotal * 0.16 // 16% VAT
  const total = subtotal + shippingCost + tax

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!supplierId) {
      toast.error('Please select a supplier')
      return
    }

    if (items.length === 0) {
      toast.error('Please add at least one item')
      return
    }

    createMutation.mutate({
      supplier_id: Number(supplierId),
      purchase_date: purchaseDate,
      purchase_number: reference,
      currency_code: currencyCode,
      exchange_rate: exchangeRate,
      notes,
      shipping_cost: shippingCost,
      other_costs: 0,
      items: items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.total,
        serials: item.serials,
        shipping_cost_allocated: item.shipping_allocated,
        landed_cost: item.landed_cost,
        suggested_retail_price: item.suggested_retail_price,
        suggested_wholesale_price: item.suggested_wholesale_price,
        margin_percent: item.margin_percent,
      })),
      tax_amount: tax,
      total_amount: total,
    })
  }

  const viewPriceHistory = (productName: string) => {
    setSelectedProductForHistory(productName)
    setShowPriceHistory(true)
  }

  const getCurrencySymbol = (code: string) => {
    return COMMON_CURRENCIES.find(c => c.code === code)?.symbol || code
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/purchases')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Purchase</h1>
          <p className="text-sm text-gray-500">Record a new purchase order with shipping and pricing</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Supplier Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Supplier Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative" ref={supplierDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                <button
                  type="button"
                  onClick={() => setShowSupplierDropdown(!showSupplierDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-left"
                >
                  <span className={selectedSupplier ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedSupplier
                      ? `${selectedSupplier.company_name} ${selectedSupplier.default_currency_code ? `(${selectedSupplier.default_currency_code})` : ''}`
                      : 'Select Supplier'}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showSupplierDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showSupplierDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search suppliers..."
                          value={supplierSearch}
                          onChange={(e) => setSupplierSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="py-1">
                      {filteredSuppliers.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No suppliers found
                        </div>
                      ) : (
                        filteredSuppliers.map((s: any) => (
                          <button
                            key={s.supplier_id}
                            type="button"
                            onClick={() => {
                              setSupplierId(s.supplier_id.toString())
                              setShowSupplierDropdown(false)
                              setSupplierSearch('')
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${supplierId === s.supplier_id.toString() ? 'bg-primary-50' : ''
                              }`}
                          >
                            <div>
                              <p className="font-medium text-sm">{s.company_name}</p>
                              {s.supplier_code && (
                                <p className="text-xs text-gray-500">{s.supplier_code}</p>
                              )}
                            </div>
                            {supplierId === s.supplier_id.toString() && (
                              <CheckIcon className="w-4 h-4 text-primary-600" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., PO-2024-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date *</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            </div>

            {supplier && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-800">
                  <CurrencyDollarIcon className="w-5 h-5" />
                  <span className="font-medium">
                    Supplier Currency: {getCurrencySymbol(supplier.default_currency_code || 'KES')} {supplier.default_currency_code || 'KES'}
                  </span>
                </div>
                <p className="text-sm text-blue-600 mt-1">
                  Exchange Rate: 1 {supplier.default_currency_code || 'KES'} = {exchangeRate} KES
                </p>
              </div>
            )}
          </div>

          {/* Currency & Shipping */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Currency & Shipping</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="relative" ref={currencyDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <button
                  type="button"
                  onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-left"
                >
                  <span>
                    {(() => {
                      const curr = COMMON_CURRENCIES.find(c => c.code === currencyCode)
                      return curr ? `${curr.symbol} ${curr.code}` : currencyCode
                    })()}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showCurrencyDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search currencies..."
                          value={currencySearch}
                          onChange={(e) => setCurrencySearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="py-1">
                      {filteredCurrencies.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No currencies found
                        </div>
                      ) : (
                        filteredCurrencies.map((curr) => (
                          <button
                            key={curr.code}
                            type="button"
                            onClick={() => {
                              setCurrencyCode(curr.code)
                              setShowCurrencyDropdown(false)
                              setCurrencySearch('')
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${currencyCode === curr.code ? 'bg-primary-50' : ''
                              }`}
                          >
                            <span className="text-sm">{curr.symbol} {curr.code} - {curr.name}</span>
                            {currencyCode === curr.code && (
                              <CheckIcon className="w-4 h-4 text-primary-600" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exchange Rate (to KES)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <TruckIcon className="w-4 h-4 inline mr-1" />
                  Shipping Cost
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Shipping cost will be allocated proportionally across all items based on their value
            </p>
          </div>

          {/* Add Items */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Items</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-1 relative" ref={productDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value)
                        setShowProductDropdown(true)
                        if (e.target.value === '') {
                          setCurrentItem(p => ({ ...p, product_id: null, product_name: '' }))
                        }
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 pr-9"
                      placeholder={currentItem.product_name || "Search products..."}
                    />
                    {currentItem.product_id && (
                      <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                  </div>

                  {showProductDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                      {productSearch.length < 2 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          Type at least 2 characters to search
                        </div>
                      ) : products.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No products found
                        </div>
                      ) : (
                        <div className="py-1">
                          {products.map((product: any) => (
                            <button
                              key={product.product_id}
                              type="button"
                              onClick={() => {
                                setCurrentItem(p => ({
                                  ...p,
                                  product_id: product.product_id,
                                  product_name: product.product_name,
                                }))
                                setProductSearch('')
                                setShowProductDropdown(false)
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                            >
                              <div>
                                <p className="font-medium text-sm">{product.product_name}</p>
                                <p className="text-xs text-gray-500">
                                  {product.sku && `SKU: ${product.sku}`}
                                  {product.sku && product.category_name && ' • '}
                                  {product.category_name}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="p-2 border-t bg-gray-50">
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentItem(p => ({ ...p, product_id: null }))
                            setProductSearch(currentItem.product_name)
                            setShowProductDropdown(false)
                          }}
                          className="w-full px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 text-left"
                        >
                          Use &quot;{productSearch || currentItem.product_name || 'custom name'}&quot; as custom product
                        </button>
                      </div>
                    </div>
                  )}

                  {currentItem.product_name && !showProductDropdown && (
                    <p className="mt-1 text-xs text-gray-500">
                      {currentItem.product_id
                        ? `Selected: ${currentItem.product_name}`
                        : `Custom product: ${currentItem.product_name}`}
                    </p>
                  )}

                  {/* Purchase History for Selected Product */}
                  {currentItem.product_id && (
                    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                        <p className="text-xs font-medium text-gray-600">Previous Purchases</p>
                        {selectedProductHistoryStats && (
                          <p className="text-xs text-gray-500">
                            Avg: {getCurrencySymbol(currencyCode)} {Number(selectedProductHistoryStats.average_cost || 0).toLocaleString()}
                          </p>
                        )}
                      </div>
                      {loadingProductHistory ? (
                        <div className="py-3 text-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto" />
                        </div>
                      ) : selectedProductHistory.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-3">No previous purchase history</p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 text-gray-500">
                              <tr>
                                <th className="px-3 py-1.5 text-left font-medium">Supplier</th>
                                <th className="px-3 py-1.5 text-left font-medium">Date</th>
                                <th className="px-3 py-1.5 text-right font-medium">Price</th>
                                <th className="px-3 py-1.5 text-right font-medium">Curr</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {selectedProductHistory.map((h: any, idx: number) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-1.5 text-gray-700 truncate max-w-[120px]">
                                    {h.purchases?.suppliers?.company_name || '—'}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">
                                    {h.purchases?.purchase_date
                                      ? new Date(h.purchases.purchase_date).toLocaleDateString()
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-medium text-gray-900">
                                    {Number(h.unit_price || 0).toLocaleString()}
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-gray-500">
                                    {h.purchases?.currency_code || 'KES'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem(p => ({ ...p, quantity: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Price ({getCurrencySymbol(currencyCode)})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentItem.unit_price}
                    onChange={(e) => setCurrentItem(p => ({ ...p, unit_price: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Margin (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={currentItem.margin_percent}
                    onChange={(e) => setCurrentItem(p => ({ ...p, margin_percent: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Serial Numbers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serial Numbers</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={serialInput}
                    onChange={(e) => setSerialInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSerial())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter serial number and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addSerial}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
                {currentItem.serials.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {currentItem.serials.map((serial, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                        {serial}
                        <button
                          type="button"
                          onClick={() => removeSerial(i)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={addItem}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600"
              >
                <PlusIcon className="w-5 h-5 inline mr-2" />
                Add Item to Purchase
              </button>
            </div>
          </div>

          {/* Items List */}
          {items.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Items ({items.length})</h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCalculator(!showCalculator)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <CalculatorIcon className="w-4 h-4" />
                    {showCalculator ? 'Hide' : 'Show'} Calculator
                  </button>
                </div>
              </div>

              {/* Global Margin Control */}
              {showCalculator && (
                <div className="p-4 bg-blue-50 border-b border-blue-100">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Apply Margin to All Items (%)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={globalMargin}
                          onChange={(e) => setGlobalMargin(Number(e.target.value))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          type="button"
                          onClick={applyGlobalMargin}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <ChartBarIcon className="w-5 h-5 inline mr-1" />
                      Selling prices include shipping allocation
                    </div>
                  </div>
                </div>
              )}

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    {showCalculator && (
                      <>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Shipping</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Landed</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Retail</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{item.product_name}</p>
                        {item.serials.length > 0 && (
                          <p className="text-xs text-gray-500">{item.serials.length} serials tracked</p>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">{item.quantity}</td>
                      <td className="px-4 py-4 text-right">
                        {getCurrencySymbol(currencyCode)} {item.unit_price.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right font-medium">
                        {getCurrencySymbol(currencyCode)} {item.total.toLocaleString()}
                      </td>
                      {showCalculator && (
                        <>
                          <td className="px-4 py-4 text-right text-sm text-orange-600">
                            {item.shipping_allocated > 0 && (
                              <span>KSh {item.shipping_allocated.toLocaleString()}</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-green-600">
                            KSh {item.landed_cost.toLocaleString()}
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-blue-600">
                            KSh {item.suggested_retail_price.toLocaleString()}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {supplierId && (
                            <button
                              type="button"
                              onClick={() => viewPriceHistory(item.product_name)}
                              className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                              title="View price history"
                            >
                              <ClockIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span>{getCurrencySymbol(currencyCode)} {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping</span>
                <span>{getCurrencySymbol(currencyCode)} {shippingCost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax (16%)</span>
                <span>{getCurrencySymbol(currencyCode)} {tax.toLocaleString()}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{getCurrencySymbol(currencyCode)} {total.toLocaleString()}</span>
                </div>
                {currencyCode !== 'KES' && (
                  <p className="text-sm text-gray-500 text-right mt-1">
                    ≈ KSh {(total * exchangeRate).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Additional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/purchases')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Purchase'}
            </button>
          </div>
        </div>
      </form>

      {/* Price History Modal */}
      {showPriceHistory && selectedProductForHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Price History: {selectedProductForHistory}
                </h3>
                <button
                  onClick={() => setShowPriceHistory(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ✕
                </button>
              </div>
              <p className="text-gray-500 mt-1">From supplier: {supplier?.company_name}</p>
            </div>
            <div className="p-6">
              {productHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No price history found for this product from this supplier
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Landed Cost</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Suggested Retail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {productHistory.map((item: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            {new Date(item.purchase_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-sm">
                            {item.currency_code} {item.unit_price.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-green-600">
                            {item.landed_cost_base ? `KSh ${item.landed_cost_base.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-blue-600">
                            {item.suggested_retail_price ? `KSh ${item.suggested_retail_price.toLocaleString()}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
